import { dehydrate, QueryClient, QueryFunction } from 'react-query';
import apis from '../../apis';
import { getAuthToken } from '@/modules/auth/utils';
import type {
  TApi,
  TApiArgs,
  TApiKeysByNamespace,
  TApiOutput,
  TNamespaces,
  TPrefetchQueriesArgs,
  TPrefetchQueriesReturn,
} from './ReactQuery.type';
import request from '../Axios';

export const prefetchQueries = (
  qArgs?: TPrefetchQueriesArgs
): TPrefetchQueriesReturn => {
  const { session, context, ...config } = qArgs || {};
  const queryClient = new QueryClient(config);
  const queries: Array<{
    params: [any[], QueryFunction, object?];
    isInfinite: boolean;
  }> = [];

  return {
    add: (keyByNamespace, args): void => {
      queries.push({
        isInfinite: !!args?.isInfinite,
        params: [
          getPreparedKey(keyByNamespace, args as any),
          async () =>
            doAPI([keyByNamespace[0], keyByNamespace[1]], {
              ...args,
              session,
              context,
            } as any),
          { retry: false /*, ...(args?.reactQueryOptions || {})*/ },
        ],
      });
    },
    dehydrate: async () => {
      await Promise.allSettled(
        queries.map(async ({ params, isInfinite }) => {
          if (isInfinite) {
            await queryClient.prefetchInfiniteQuery(...params);
            await queryClient.setQueryData(params[0], (data: any) => ({
              ...(data || {}),
              pageParams: [],
            }));
          } else {
            await queryClient.prefetchQuery(...params);
          }
        })
      );

      const dehydratedState = await dehydrate(queryClient);

      const queriesData = dehydratedState?.queries?.map(
        (query) => query.state.data
      );

      return {
        dehydratedState,
        queriesData,
      };
    },
  };
};

export const doAPI = async <
  Namespace extends TNamespaces,
  Key extends TApiKeysByNamespace<Namespace>,
>(
  keyByNamespace: [Namespace, Key],
  args: Pick<
    TApiArgs<Namespace, Key, false, false>,
    | 'input'
    | 'axiosRequestConfig'
    | 'session'
    | 'context'
    | 'resolveInterceptor'
    | 'rejectInterceptor'
  >
): Promise<TApiOutput<Namespace, Key>> => {
  const [namespace, key] = keyByNamespace;

  const {
    input,
    session,
    context,
    axiosRequestConfig: argsAxiosRequestConfig,
    resolveInterceptor: argsResolveInterceptor,
    rejectInterceptor: argsRejectInterceptor,
  } = args || {};
  const {
    resolveInterceptor: apiResolveInterceptor,
    rejectInterceptor: apiRejectInterceptor,
    anonymousUrl,
    ...apiAxiosRequestConfig
  } = (apis[namespace][key] as any)(input) as ReturnType<TApi<any, any>>;

  const { token } = session || (context ? getAuthToken(context) : null) || {};

  const resolveInterceptor = argsResolveInterceptor ?? apiResolveInterceptor;
  const rejectInterceptor = argsRejectInterceptor ?? apiRejectInterceptor;

  const mergedAxiosRequestConfig = Object.assign(
    { headers: {} },
    apiAxiosRequestConfig,
    argsAxiosRequestConfig,
    [apiAxiosRequestConfig, argsAxiosRequestConfig].reduce(
      (previous, current) =>
        require('axios').mergeConfig({ ...previous }, { ...current }),
      {}
    )
  );

  if (token && !('Authorization' in mergedAxiosRequestConfig.headers)) {
    mergedAxiosRequestConfig.headers['Authorization'] = `Bearer ${token}`;
  }

  if (!token && !!anonymousUrl) {
    // * If token doesn't exist and the api has anonymous url, it should use anonymous url as the url
    mergedAxiosRequestConfig.url = anonymousUrl;
  }

  return request({
    baseURL:
      typeof window === 'undefined' && process.env.API_SSR_BASE_URL
        ? process.env.API_SSR_BASE_URL
        : process.env.NEXT_PUBLIC_API_CSR_BASE_URL,
    ...mergedAxiosRequestConfig,
    resolveInterceptor,
    rejectInterceptor,
  });
};

export const getPreparedKey = <
  Namespace extends TNamespaces,
  Key extends TApiKeysByNamespace<Namespace>,
  IsMutation extends boolean,
  IsInfinite extends boolean,
>(
  keyByNamespace: [Namespace, Key, ...any[]],
  args: Pick<
    TApiArgs<Namespace, Key, IsMutation, IsInfinite>,
    'input' | 'isMutation' | 'isInfinite'
  >
) => {
  const { isMutation, isInfinite } = args || {};
  if (keyByNamespace.length === 2 && args?.input)
    keyByNamespace.push(args.input);
  if (isMutation) keyByNamespace.push('Mutation');
  if (isInfinite) keyByNamespace.push('Infinite');
  return keyByNamespace;
};
